/**
 * useActionQueue — 통합 액션 큐 상태/액션 훅 (대시보드 재설계 Sprint 3)
 *
 * - 큐 fetch + useAdminInboxCounts(20초 폴링·SSE·BroadcastChannel)의 unresolved 변화 감지로 재조회
 * - done/snooze 옵티미스틱 제거 (실패 시 롤백 + toast)
 * - AI 브리핑은 큐 렌더를 막지 않도록 비동기 분리 (응답 수 초 소요 가능)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import actionQueueService, {
  ActionQueueItem,
  ActionQueuePayload,
  QueueBriefing,
} from '../../../services/actionQueue';
import { useAdminInboxCounts, broadcastInboxAction } from '../../../hooks/useAdminInboxCounts';

export function useActionQueue() {
  const [queue, setQueue] = useState<ActionQueuePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [briefing, setBriefing] = useState<QueueBriefing | null>(null);
  const [briefingLoading, setBriefingLoading] = useState(false);

  const { counts } = useAdminInboxCounts(20_000);
  const lastUnresolvedRef = useRef<number | null>(null);
  const briefingFetchedRef = useRef(false);

  const fetchQueue = useCallback(async (refresh = false) => {
    try {
      setError('');
      const data = await actionQueueService.getQueue(refresh);
      setQueue(data);
      return data;
    } catch (e: any) {
      setError(e?.message || '액션 큐 조회 실패');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBriefing = useCallback(async (refresh = false) => {
    setBriefingLoading(true);
    try {
      const data = await actionQueueService.getBriefing(refresh);
      setBriefing(data);
    } catch {
      setBriefing(null); // 브리핑 실패는 조용히 — 큐 본체와 무관
    } finally {
      setBriefingLoading(false);
    }
  }, []);

  // 최초 로드: 큐 먼저, 브리핑은 비차단 후속
  useEffect(() => {
    (async () => {
      const data = await fetchQueue();
      if (!briefingFetchedRef.current) {
        briefingFetchedRef.current = true;
        if (data && data.total > 0) fetchBriefing();
      }
    })();
  }, [fetchQueue, fetchBriefing]);

  // 인박스 unresolved 변화 감지 → 큐 재조회 (SSE/폴링/타 탭 액션의 즉각 반영)
  useEffect(() => {
    const cur = counts.unresolved ?? 0;
    if (lastUnresolvedRef.current === null) {
      lastUnresolvedRef.current = cur;
      return;
    }
    if (lastUnresolvedRef.current !== cur) {
      lastUnresolvedRef.current = cur;
      fetchQueue(true);
    }
  }, [counts.unresolved, fetchQueue]);

  /** 옵티미스틱 제거 헬퍼 — 실패 시 원복용 스냅샷 반환 */
  const removeItemLocally = useCallback((hash: string) => {
    let snapshot: ActionQueuePayload | null = null;
    setQueue((prev) => {
      if (!prev) return prev;
      snapshot = prev;
      const groups = prev.groups.map((g) => {
        const items = g.items.filter((it) => it.hash !== hash);
        return { ...g, items, count: items.length };
      });
      return { ...prev, groups, total: groups.reduce((s, g) => s + g.count, 0) };
    });
    return () => setQueue(snapshot);
  }, []);

  /**
   * 완료 처리 결과:
   *  - 'done'       : 정상 완료 → 컴포넌트가 "실행취소" 스낵바 표시
   *  - 'idempotent' : 이미 다른 담당자가 완료 → 실행취소 없는 안내 토스트 (undo 무효)
   *  - 'fail'       : 실패(롤백 + 에러 토스트)
   */
  const markDone = useCallback(
    async (item: ActionQueueItem): Promise<'done' | 'idempotent' | 'fail'> => {
      const rollback = removeItemLocally(item.hash);
      try {
        const { done_today, idempotent } = await actionQueueService.done(item);
        if (idempotent) {
          // 타인이 이미 완료 — 낙관적 제거는 유지하고 서버 기준으로 큐 재조회(정합)
          setQueue((prev) => (prev ? { ...prev, done_today } : prev));
          await fetchQueue(true);
          broadcastInboxAction();
          return 'idempotent';
        }
        setQueue((prev) => (prev ? { ...prev, done_today } : prev));
        broadcastInboxAction(); // 타 탭/인박스 카운트 즉시 동기화
        return 'done'; // "완료 처리했습니다 — 실행취소" 스낵바는 컴포넌트가 표시
      } catch (e: any) {
        rollback();
        toast.error(e?.message || '완료 처리 실패');
        return 'fail';
      }
    },
    [removeItemLocally, fetchQueue],
  );

  /**
   * 완료 실행취소(undo) — 스낵바의 "실행취소" 클릭 시 호출.
   * restored:true → 큐 재조회로 복원 항목 반영 + 타 탭 동기화, restored:false → 사유 안내.
   */
  const undo = useCallback(
    async (item: ActionQueueItem) => {
      try {
        const result = await actionQueueService.undo(item);
        if (result.restored) {
          await fetchQueue(true); // 서버 기준 재조회 — 복원된 항목·done_today 반영
          broadcastInboxAction();
          toast.success('복원했습니다');
        } else {
          // 복원 불가(타 담당자 선완료 등) — done_today 는 서버 최신값 반영 후 사유 안내
          setQueue((prev) => (prev ? { ...prev, done_today: result.done_today } : prev));
          toast(result.reason || '완료 취소를 처리할 수 없습니다', { icon: 'ℹ️' });
        }
      } catch (e: any) {
        toast.error(e?.message || '완료 취소 실패');
      }
    },
    [fetchQueue],
  );

  const snoozeToday = useCallback(
    async (item: ActionQueueItem) => {
      const rollback = removeItemLocally(item.hash);
      try {
        await actionQueueService.snooze(item);
        toast.success('오늘 하루 숨겼습니다');
      } catch (e: any) {
        rollback();
        toast.error(e?.message || '숨김 처리 실패');
      }
    },
    [removeItemLocally],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    await fetchQueue(true);
    fetchBriefing(false);
  }, [fetchQueue, fetchBriefing]);

  return {
    queue,
    loading,
    error,
    briefing,
    briefingLoading,
    unreadCount: counts.unread ?? 0,
    refresh,
    retry: () => {
      setLoading(true);
      fetchQueue(true);
    },
    markDone,
    undo,
    snoozeToday,
  };
}

export default useActionQueue;
